import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ConsumerJourneyState,
  JourneyStage,
  JourneyStageData,
} from '@/types/consumerJourney';
import {
  fetchConsumerJourneyGrouping,
  createAllJourneyElements,
  regenerateStageAnalysis,
  fetchAllJourneyElements,
} from '../asyncActions/consumerJourneyActions';

const initialState: ConsumerJourneyState = {
  // Grouping dialog state
  groupingLoading: false,
  suggestedGroups: [],
  groupingError: null,

  // Element creation state
  creatingElements: false,
  creationProgress: {
    total: 0,
    completed: 0,
    currentStage: '',
  },

  // Journey data state
  journeyElements: [],
  journeyLoading: false,

  // Edit state
  editingStage: null,
  regenerating: false,

  // Dialog states
  showGroupingDialog: false,
  showEditModal: false,
};

export const ConsumerJourneySlice = createSlice({
  name: 'consumerJourney',
  initialState,
  reducers: {
    openGroupingDialog: (state) => {
      state.showGroupingDialog = true;
    },
    closeGroupingDialog: (state) => {
      state.showGroupingDialog = false;
      state.suggestedGroups = [];
      state.groupingError = null;
    },
    updateSuggestedGroups: (state, action: PayloadAction<JourneyStage[]>) => {
      state.suggestedGroups = action.payload;
    },
    openEditModal: (state, action: PayloadAction<JourneyStageData>) => {
      state.editingStage = action.payload;
      state.showEditModal = true;
    },
    closeEditModal: (state) => {
      state.editingStage = null;
      state.showEditModal = false;
    },
    setJourneyElements: (state, action: PayloadAction<JourneyStageData[]>) => {
      state.journeyElements = action.payload;
    },
    updateJourneyElement: (state, action: PayloadAction<JourneyStageData>) => {
      const index = state.journeyElements.findIndex(
        (el) => el.elementId === action.payload.elementId,
      );
      if (index !== -1) {
        state.journeyElements[index] = action.payload;
      }
    },
    clearJourneyData: (state) => {
      state.journeyElements = [];
      state.suggestedGroups = [];
      state.groupingError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch grouping
      .addCase(fetchConsumerJourneyGrouping.pending, (state) => {
        state.groupingLoading = true;
        state.groupingError = null;
      })
      .addCase(fetchConsumerJourneyGrouping.fulfilled, (state, action) => {
        state.groupingLoading = false;
        if (action.payload.status && action.payload.data) {
          state.suggestedGroups = action.payload.data.suggestedGroups || [];
          if (action.payload.data.error) {
            state.groupingError = action.payload.data.error;
          }
        } else {
          state.groupingError = 'Failed to get AI suggestions';
        }
      })
      .addCase(fetchConsumerJourneyGrouping.rejected, (state, action) => {
        state.groupingLoading = false;
        state.groupingError = action.error.message || 'Failed to fetch groupings';
      })
      // Create all elements
      .addCase(createAllJourneyElements.pending, (state, action) => {
        state.creatingElements = true;
        state.creationProgress = {
          total: action.meta.arg.stages.length,
          completed: 0,
          currentStage: 'Creating elements...',
        };
      })
      .addCase(createAllJourneyElements.fulfilled, (state) => {
        state.creatingElements = false;
        state.creationProgress = {
          total: 0,
          completed: 0,
          currentStage: '',
        };
        state.showGroupingDialog = false;
      })
      .addCase(createAllJourneyElements.rejected, (state) => {
        state.creatingElements = false;
      })
      // Fetch all journey elements
      .addCase(fetchAllJourneyElements.pending, (state) => {
        state.journeyLoading = true;
      })
      .addCase(fetchAllJourneyElements.fulfilled, (state, action) => {
        state.journeyLoading = false;
        // Transform the responses into JourneyStageData
        const elements: JourneyStageData[] = action.payload.map((response: any) => {
          const element = response.element || response;
          const filterObject = element.filterObject || {};
          const aiAnalysis = element.aiAnalysis || {};

          return {
            elementId: element.reportElementId || element.id,
            stageId: filterObject.stageId || '',
            stageName: filterObject.stageName || element.title || '',
            order: element.orderId || 0,
            questions: filterObject.questionIds?.map((qId: number) => ({
              questionId: qId,
              questionText: '',
              answerType: '',
            })) || [],
            scaleType: filterObject.scaleType || '0-10',
            userPreference: filterObject.userPreference || '',
            scale: aiAnalysis.scale,
            scaleMax: filterObject.scaleType === '0-100' ? 100 : filterObject.scaleType === '1-5' ? 5 : 10,
            summary: aiAnalysis.summary || '',
          };
        });
        state.journeyElements = elements;
      })
      .addCase(fetchAllJourneyElements.rejected, (state) => {
        state.journeyLoading = false;
      })
      // Regenerate stage analysis
      .addCase(regenerateStageAnalysis.pending, (state) => {
        state.regenerating = true;
      })
      .addCase(regenerateStageAnalysis.fulfilled, (state, action) => {
        state.regenerating = false;
        // Update the editing stage with new AI analysis
        if (state.editingStage && action.payload.status && action.payload.data) {
          const updatedStage = {
            ...state.editingStage,
            scale: action.payload.data.scale,
            summary: action.payload.data.summary,
          };
          state.editingStage = updatedStage;

          // Also update in journeyElements array
          const index = state.journeyElements.findIndex(
            (el) => el.elementId === state.editingStage?.elementId,
          );
          if (index !== -1) {
            state.journeyElements[index] = updatedStage;
          }
        }
      })
      .addCase(regenerateStageAnalysis.rejected, (state) => {
        state.regenerating = false;
      });
  },
});

export const { actions: consumerJourneyActions } = ConsumerJourneySlice;
export default ConsumerJourneySlice.reducer;
